import React, { useState, useEffect } from 'react';
import { Mail, Clock, Send, MessageSquare } from 'lucide-react';
import { useUserStore } from '../../../stores/userStore';
import LiveChatSupport from '../support/LiveChatSupport';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function Support() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: 'technical',
    message: ''
  });
  
  // Fix: Correctly access the isPro and isAdmin functions from the store
  const isPro = useUserStore(state => state.isPro);
  const isAdmin = useUserStore(state => state.isAdmin);
  const isPremium = useUserStore(state => state.isPremium);
  const currentUser = useUserStore(state => state.currentUser);
  
  // Check if user has access to Pro support (either Pro user or Admin)
  const hasProSupport = isPro() || isAdmin();
  const hasPremiumSupport = isPremium() || hasProSupport;

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.name,
        email: currentUser.email
      }));
    }
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Initialize EmailJS with your public key
      emailjs.init("sXSuVLEOKVknSCXtn");

      // Determine the recipient email based on user role
      let to_email = "team.novacontent@gmail.com";
      
      // Get template ID based on user role
      let templateId = "template_k6v3ifg"; // Default template

      // Send email using EmailJS
      await emailjs.send(
        "service_ej2yppg",
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_email: to_email,
          user_role: hasPremiumSupport ? "premium" : "free"
        }
      );

      toast.success('Obrigado! Sua mensagem foi enviada. Entraremos em contato em breve.');
      setFormData({
        name: currentUser?.name || '',
        email: currentUser?.email || '',
        subject: 'technical',
        message: ''
      });
    } catch (error) {
      toast.error('Erro ao enviar mensagem. Por favor, tente novamente.');
      console.error('Email error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Pro Live Chat Banner */}
      {hasProSupport && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Suporte Pro por Chat ao Vivo</h3>
                <p className="text-gray-600">
                  {isAdmin() 
                    ? "Como administrador, você tem acesso ao nosso suporte prioritário por chat ao vivo"
                    : "Como assinante Pro, você tem acesso ao nosso suporte prioritário por chat ao vivo"}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowLiveChat(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              Iniciar Chat
              <MessageSquare className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Premium Support Banner */}
      {!hasProSupport && hasPremiumSupport && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Mail className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Suporte Premium</h3>
              <p className="text-gray-600">
                Como assinante Premium, você tem acesso ao nosso suporte por email rápido com tempo de resposta prioritário
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contact Information */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Entre em Contato
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-1">Email</h3>
              <a 
                href="mailto:team.novacontent@gmail.com"
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                team.novacontent@gmail.com
              </a>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-1">Horário</h3>
              <p className="text-gray-600 dark:text-gray-400">24/7</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Formulário de Contato
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Assunto
            </label>
            <select
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="technical">Problema Técnico</option>
              <option value="billing">Dúvida sobre Pagamento</option>
              <option value="general">Pergunta Geral</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Mensagem
            </label>
            <textarea
              required
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              'Enviando...'
            ) : (
              <>
                Enviar Mensagem
                <Send className="ml-2 h-4 w-4" />
              </>
            )}
          </button>
        </form>
      </div>

      {/* Response Time Notice */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Clock className="h-5 w-5 text-gray-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-gray-900">Tempo de Resposta</h4>
            <p className="text-gray-600 text-sm mt-1">
              {hasProSupport 
                ? 'Como usuário Pro, você receberá uma resposta em até 4 horas úteis.'
                : hasPremiumSupport
                ? 'Como usuário Premium, você receberá uma resposta em até 12 horas úteis.'
                : 'Nosso tempo médio de resposta é de 24-48 horas úteis.'}
            </p>
          </div>
        </div>
      </div>

      {/* Live Chat Support */}
      {showLiveChat && <LiveChatSupport onClose={() => setShowLiveChat(false)} />}
    </div>
  );
}